import { cn } from "@workspace/ui/lib/utils";
import type { HTMLAttributes } from "react";

export type LoaderProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
  variant?: "dots" | "pulse" | "typing";
};

export const Loader = ({
  className,
  size = 16,
  variant = "dots",
  ...props
}: LoaderProps) => {
  if (variant === "typing") {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-0.5 text-muted-foreground",
          className
        )}
        {...props}
      >
        <span className="text-sm">Thinking</span>
        <span className="inline-flex">
          <span
            className="animate-bounce"
            style={{ animationDelay: "0ms", animationDuration: "1s" }}
          >
            .
          </span>
          <span
            className="animate-bounce"
            style={{ animationDelay: "150ms", animationDuration: "1s" }}
          >
            .
          </span>
          <span
            className="animate-bounce"
            style={{ animationDelay: "300ms", animationDuration: "1s" }}
          >
            .
          </span>
        </span>
      </div>
    );
  }

  if (variant === "pulse") {
    return (
      <div
        className={cn("inline-flex items-center gap-1", className)}
        {...props}
      >
        <div
          className="rounded-full bg-current animate-pulse"
          style={{ width: size / 2, height: size / 2 }}
        />
        <span className="text-sm text-muted-foreground">Generating...</span>
      </div>
    );
  }

  // Default: dots variant
  return (
    <div
      className={cn("inline-flex items-center justify-center", className)}
      {...props}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes hop {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .hop-animation { animation: hop 1.4s ease-in-out infinite; }
      `,
        }}
      />
      <div className="flex items-center gap-0.5">
        {[0, 200, 400].map((delay) => (
          <span
            className="rounded-full bg-muted-foreground hop-animation"
            key={delay}
            style={{
              animationDelay: `${delay}ms`,
              width: size / 3,
              height: size / 3,
            }}
          />
        ))}
      </div>
    </div>
  );
};
